import cn from "classnames";
import {
  ColumnDef,
  Table,
  TableState,
  createColumnHelper,
  getCoreRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { t } from "i18next";
import { LocalizedString } from "oneclick-component/src/store/apis/enhancedApi";
import { localizeString } from "oneclick-component/src/utils/localize";

export interface PaidMessageSentListRecord {
  name: LocalizedString;
  phoneNumber: string;
  station: string;
  paidMessageSent: number;
  lastReplyTimestamp: string | null;
}

const columnHelper = createColumnHelper<PaidMessageSentListRecord>();

export const usePaidMessageSentListTableColumns = (): ColumnDef<
  PaidMessageSentListRecord,
  any
>[] => {
  return [
    columnHelper.accessor("name", {
      header: () => t("paidMessageSentList.table.header.name"),
      cell: ({ cell }) => {
        const name = cell.getValue() as PaidMessageSentListRecord["name"];
        return (
          <div
            className={cn(
              "py-3",
              "px-6",
              "font-normal",
              "text-sm",
              "leading-5",
              "text-black/86"
            )}
          >
            {localizeString(name)}
          </div>
        );
      },
    }),
    columnHelper.accessor("phoneNumber", {
      header: () => t("paidMessageSentList.table.header.phoneNumber"),
      cell: ({ cell }) => {
        const phoneNumber =
          cell.getValue() as PaidMessageSentListRecord["phoneNumber"];
        return (
          <div
            className={cn(
              "py-3",
              "px-6",
              "font-normal",
              "text-sm",
              "leading-5",
              "text-black/86"
            )}
          >
            {phoneNumber}
          </div>
        );
      },
    }),
    columnHelper.accessor("station", {
      header: () => t("paidMessageSentList.table.header.station"),
      cell: ({ cell }) => {
        const station = cell.getValue() as PaidMessageSentListRecord["station"];
        return (
          <div
            className={cn(
              "py-3",
              "px-6",
              "font-normal",
              "text-sm",
              "leading-5",
              "text-black/86",
              "flex",
              "flex-row",
              "flex-wrap",
              "gap-x-1"
            )}
          >
            {station}
          </div>
        );
      },
    }),
    columnHelper.accessor("paidMessageSent", {
      header: () => t("paidMessageSentList.table.header.paidMessageSent"),
      cell: ({ cell }) => {
        return (
          <p
            className={cn(
              "py-3",
              "px-6",
              "font-normal",
              "text-sm",
              "leading-5",
              "text-black/86"
            )}
          >
            {cell.getValue()}
          </p>
        );
      },
    }),
    columnHelper.accessor("lastReplyTimestamp", {
      header: () => t("paidMessageSentList.table.header.lastReplyTimestamp"),
      cell: ({ cell }) => {
        const lastReplyTimestamp =
          cell.getValue() as PaidMessageSentListRecord["lastReplyTimestamp"];
        return (
          <p
            className={cn(
              "py-3",
              "px-6",
              "font-normal",
              "text-sm",
              "leading-5",
              "text-black/86",
              "whitespace-nowrap"
            )}
          >
            {lastReplyTimestamp ?? "-"}
          </p>
        );
      },
    }),
  ];
};

export const usePaidMessageSentListTable = (
  data: PaidMessageSentListRecord[],
  state?: Partial<TableState>
): Table<PaidMessageSentListRecord> => {
  const shiftListTableColumns = usePaidMessageSentListTableColumns();
  const table = useReactTable({
    data,
    columns: shiftListTableColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    defaultColumn: {
      minSize: 0,
      size: Number.MAX_SAFE_INTEGER,
      maxSize: Number.MAX_SAFE_INTEGER,
    },
    manualSorting: true,
    groupedColumnMode: false,
    enableRowSelection: true,
    state,
  });
  return table;
};
